import type { ReactNode } from "react";
import {
  Pressable,
  Text,
  View,
  type AccessibilityRole,
  type AccessibilityState,
  type GestureResponderEvent,
  type StyleProp,
  type TextStyle,
  type ViewStyle,
} from "react-native";
import { ChevronRight } from "lucide-react-native";
import { radii, semanticColors } from "../theme/tokens";
import { CARD_RADIUS, cardSurface } from "./CardSurface";
import { PrimaryTitleText } from "./PrimaryTitleText";
import { SupportingSubtitleText } from "./SupportingSubtitleText";
import {
  pressLabel,
  usePressCapture,
} from "@src/packages/analytics/usePressCapture";
import { MetaText } from "./MetaText";

/**
 * `glass` is the default card row. `plain` has no surface of its own. `input`
 * is the flat, bordered fill fields use: pick it inside a dialog's scroll
 * area, which clips a glass row's shadow into a grey band.
 */
export type RowSurface = "glass" | "plain" | "input";
export type RowDensity = "default" | "dense";
export type RowSubtitleVariant = "supporting" | "meta";

export type RowProps = {
  surface?: RowSurface;
  /**
   * Glass rows cast a soft shadow. Turn it off for glass rows listed inside
   * a dialog's scroll area, which clips the shadow into a grey band.
   */
  elevated?: boolean;
  /** Dense is for nested rows only; it never changes the global row default. */
  density?: RowDensity;
  subtitleVariant?: RowSubtitleVariant;
  leadingMeta?: ReactNode;
  leading?: ReactNode;
  title: ReactNode;
  subtitle?: ReactNode;
  titleRight?: ReactNode;
  trailing?: ReactNode;
  chevron?: boolean;
  chevronColor?: string;
  meta?: ReactNode;
  onPress?: (event: GestureResponderEvent) => void;
  /**
   * Stable analytics identifier for the row. The row's visible title travels
   * as the event's `label`; this is the half that survives a copy edit or a
   * locale change, and it is what a funnel groups on.
   */
  action?: string;
  disabled?: boolean;
  muted?: boolean;
  accessibilityRole?: AccessibilityRole;
  accessibilityLabel?: string;
  accessibilityState?: AccessibilityState;
  testID?: string;
  leadingMetaWidth?: number;
  leadingSize?: number;
  gap?: number;
  paddingHorizontal?: number;
  paddingVertical?: number;
  borderRadius?: number;
  titleNumberOfLines?: number;
  subtitleNumberOfLines?: number;
  style?: StyleProp<ViewStyle>;
  titleStyle?: StyleProp<TextStyle>;
  subtitleStyle?: StyleProp<TextStyle>;
  leadingMetaStyle?: StyleProp<TextStyle>;
};

function renderTextLike(
  value: ReactNode,
  style: StyleProp<TextStyle>,
  numberOfLines?: number,
  kind: "default" | "title" | "subtitle" | "meta" = "default",
) {
  if (typeof value === "string" || typeof value === "number") {
    if (kind === "title") {
      return (
        <PrimaryTitleText numberOfLines={numberOfLines} style={style}>
          {value}
        </PrimaryTitleText>
      );
    }
    if (kind === "subtitle") {
      return (
        <SupportingSubtitleText numberOfLines={numberOfLines} style={style}>
          {value}
        </SupportingSubtitleText>
      );
    }
    if (kind === "meta") {
      return (
        <MetaText numberOfLines={numberOfLines} style={style}>
          {value}
        </MetaText>
      );
    }
    return (
      <Text
        className="font-dm-sans"
        numberOfLines={numberOfLines}
        style={style}
      >
        {value}
      </Text>
    );
  }
  return value;
}

export function Row({
  surface = "glass",
  elevated = true,
  density = "default",
  subtitleVariant = "supporting",
  leadingMeta,
  leading,
  title,
  subtitle,
  titleRight,
  trailing,
  chevron,
  chevronColor = semanticColors.text.tertiary,
  meta,
  onPress,
  action,
  disabled = false,
  muted = false,
  accessibilityRole,
  accessibilityLabel,
  accessibilityState,
  testID,
  leadingMetaWidth = 42,
  leadingSize = 34,
  gap = 12,
  paddingHorizontal = 16,
  paddingVertical = 13,
  borderRadius = CARD_RADIUS,
  titleNumberOfLines,
  subtitleNumberOfLines,
  style,
  titleStyle,
  subtitleStyle,
  leadingMetaStyle,
}: RowProps) {
  const showChevron = chevron ?? !!onPress;
  const isPressable = !!onPress && !disabled;
  const capturePress = usePressCapture("Row");
  const resolvedAction = action ?? "row";
  const resolvedTitleStyle: StyleProp<TextStyle> =
    density === "dense"
      ? [
          {
            fontSize: 12.5,
            lineHeight: 15,
            fontWeight: "800" as const,
          },
          titleStyle,
        ]
      : titleStyle;
  const resolvedSubtitleStyle: StyleProp<TextStyle> =
    density === "dense"
      ? [
          {
            fontSize: 11,
            lineHeight: 14,
          },
          subtitleStyle,
        ]
      : subtitleStyle;

  const body = (
    <>
      {meta}
      <View
        style={[
          {
            flexDirection: "row",
            alignItems: "center",
            gap,
          },
        ]}
      >
        {leadingMeta ? (
          <View style={{ width: leadingMetaWidth, flexShrink: 0 }}>
            {renderTextLike(
              leadingMeta,
              [
                {
                  fontSize: 11,
                  lineHeight: 15,
                  color: semanticColors.text.tertiary,
                  fontWeight: "800",
                },
                leadingMetaStyle,
              ],
              2,
            )}
          </View>
        ) : null}

        {leading ? (
          <View
            style={{
              width: leadingSize,
              height: leadingSize,
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            {leading}
          </View>
        ) : null}

        <View style={{ flex: 1, minWidth: 0 }}>
          <View
            style={{
              flexDirection: "row",
              alignItems: "center",
              gap: 7,
              flexWrap: "wrap",
            }}
          >
            {renderTextLike(
              title,
              resolvedTitleStyle,
              titleNumberOfLines,
              "title",
            )}
            {titleRight}
          </View>
          {subtitle
            ? renderTextLike(
                subtitle,
                resolvedSubtitleStyle,
                subtitleNumberOfLines,
                subtitleVariant === "meta" ? "meta" : "subtitle",
              )
            : null}
        </View>

        {trailing}
        {showChevron ? (
          <ChevronRight size={15} color={chevronColor} strokeWidth={1.7} />
        ) : null}
      </View>
    </>
  );

  const baseStyle: ViewStyle = {
    ...(surface === "glass"
      ? {
          ...cardSurface(),
          ...(elevated ? null : { shadowOpacity: 0 }),
        }
      : surface === "input"
        ? {
            backgroundColor: semanticColors.surface.input,
            borderWidth: 1,
            borderColor: semanticColors.border.subtle,
          }
        : {}),
    borderRadius: surface === "glass" ? borderRadius : radii.md,
    paddingHorizontal,
    paddingVertical,
    gap: meta ? 10 : 0,
    opacity: disabled ? 0.5 : muted ? 0.72 : 1,
  };

  // react-native-web 0.21 ignores accessibilityState, so a checked,
  // selected or disabled row mirrors it as the aria attribute the DOM reads.
  const ariaState = {
    "aria-checked": accessibilityState?.checked,
    "aria-selected": accessibilityState?.selected,
    "aria-disabled": accessibilityState?.disabled,
  };

  if (isPressable) {
    return (
      <Pressable
        testID={testID}
        accessibilityRole={accessibilityRole ?? "button"}
        accessibilityLabel={accessibilityLabel}
        accessibilityState={accessibilityState}
        {...ariaState}
        onPress={(event) => {
          capturePress(
            pressLabel(title, accessibilityLabel, testID),
            resolvedAction,
          );
          onPress?.(event);
        }}
        disabled={disabled}
        style={({ pressed }) => [
          baseStyle,
          pressed
            ? {
                opacity: 0.86,
                transform: surface === "glass" ? [{ scale: 0.995 }] : undefined,
              }
            : undefined,
          style,
        ]}
      >
        {body}
      </Pressable>
    );
  }

  return (
    <View
      testID={testID}
      accessibilityRole={accessibilityRole}
      accessibilityLabel={accessibilityLabel}
      accessibilityState={accessibilityState}
      {...ariaState}
      style={[baseStyle, style]}
    >
      {body}
    </View>
  );
}

export default Row;
